// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);     // {id, email, name, department, duty, role, password_changed ...}
  const [session, setSession] = useState(null); // Supabase session
  const [loading, setLoading] = useState(true);

  // Supabase 인증 상태 변경 감지
  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    // 초기 세션 확인
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        // Supabase user와 metadata를 기존 형식으로 변환
        const userData = {
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.name || session.user.user_metadata?.full_name || '',
          department: session.user.user_metadata?.department || null,
          duty: session.user.user_metadata?.duty || null,
          role: session.user.user_metadata?.role || 'user',
          password_changed: session.user.user_metadata?.password_changed !== false, // 기본값 true (false일 때만 재설정 필요)
        };
        setUser(userData);
      }
      setLoading(false);
    });

    // 인증 상태 변경 리스너
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        window.sessionStorage.setItem("password_recovery_mode", "1");
      }
      setSession(session);
      if (session?.user) {
        const userData = {
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.name || session.user.user_metadata?.full_name || '',
          department: session.user.user_metadata?.department || null,
          duty: session.user.user_metadata?.duty || null,
          role: session.user.user_metadata?.role || 'user',
          password_changed: session.user.user_metadata?.password_changed !== false, // 기본값 true (false일 때만 재설정 필요)
        };
        setUser(userData);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email, password) => {
    if (!supabase) {
      throw new Error('Supabase가 설정되지 않았습니다. REACT_APP_SUPABASE_URL과 REACT_APP_SUPABASE_ANON_KEY를 설정해주세요.');
    }
    // 디버깅: 로그인 시도 정보 (개발 환경에서만)
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 로그인 시도:', { email, passwordLength: password?.length });
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password: password.trim(),
    });

    if (error) {
      // 디버깅: 상세 오류 정보 (개발 환경에서만)
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ Supabase 로그인 오류:', {
          status: error.status,
          message: error.message,
          name: error.name,
        });
      }

      // 더 명확한 오류 메시지 제공
      let errorMessage = error.message;
      
      if (error.status === 401 || error.status === 400) {
        if (error.message.includes('Invalid login credentials') || 
            error.message.includes('Invalid') ||
            error.message.includes('email') ||
            error.message.includes('password')) {
          errorMessage = "이메일 또는 비밀번호가 올바르지 않습니다. 임시 비밀번호는 'password123'입니다.";
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = "이메일 인증이 완료되지 않았습니다. 이메일을 확인해주세요.";
        } else {
          errorMessage = "로그인에 실패했습니다. 이메일과 비밀번호를 확인해주세요.";
        }
      }
      
      const customError = new Error(errorMessage);
      customError.originalError = error;
      throw customError;
    }

    if (data?.session?.user) {
      const userData = {
        id: data.session.user.id,
        email: data.session.user.email,
        name: data.session.user.user_metadata?.name || data.session.user.user_metadata?.full_name || '',
        department: data.session.user.user_metadata?.department || null,
        duty: data.session.user.user_metadata?.duty || null,
        role: data.session.user.user_metadata?.role || 'user',
        password_changed: data.session.user.user_metadata?.password_changed !== false, // 기본값 true (false일 때만 재설정 필요)
      };
      setUser(userData);
      setSession(data.session);
    }

    return { user: data?.user, session: data?.session };
  };

  const updatePassword = async (newPassword) => {
    if (!supabase) {
      throw new Error('Supabase가 설정되지 않았습니다. 환경 변수를 확인해주세요.');
    }
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
      data: {
        password_changed: true, // 비밀번호 변경 완료 플래그
      },
    });

    if (error) {
      throw error;
    }

    // 사용자 정보 갱신
    if (data?.user) {
      const userData = {
        id: data.user.id,
        email: data.user.email,
        name: data.user.user_metadata?.name || data.user.user_metadata?.full_name || '',
        department: data.user.user_metadata?.department || null,
        duty: data.user.user_metadata?.duty || null,
        role: data.user.user_metadata?.role || 'user',
        password_changed: true,
      };
      setUser(userData);
    }

    return data;
  };

  const logout = async () => {
    if (supabase) {
      await supabase.auth.signOut();
    }
    setUser(null);
    setSession(null);
  };

  const value = {
    user,
    session,
    token: session?.access_token || null,
    isAuthenticated: !!user && !!session,
    login,
    logout,
    updatePassword,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <div>인증 상태 확인 중...</div> : children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}